/**
 * BPR (Business Process Review) contracts
 * ---------------------------------------
 * Zod schemas + types for the BPR module: the AI-generated framework document
 * (the "inverted triangle": functional areas -> ranked issues -> top solution
 * patterns), override patches, the source registry, and the engagement-side
 * entities (engagement, session, note, finding, recommendation, output).
 *
 * These are the single source of truth for the enum-ish text columns on the
 * knowledge.bpr_framework_* and sales.bpr_* tables (repo rule: no DB
 * value-enum CHECK constraints; route allowlists re-check these sets).
 *
 * The framework content schema is what the generate-bpr-framework skill emits
 * and what knowledge.bpr_framework_version.content stores. It is kept
 * PERMISSIVE (no .refine()/cross-field rules) so the AI runtime can feed
 * validation issues back to the model for self-correction, mirroring the
 * reference-estimate corpus schemas.
 *
 * Key invariants encoded here:
 * - Framework nodes carry STABLE KEYS (slug-like text). Findings and
 *   recommendations reference nodes by key within an engagement's PINNED
 *   framework snapshot - never by FK to mutable rows.
 * - Every generated node carries citations + confidence so a consultant can
 *   audit it at the trust gate before approval.
 * - estimateConfigRef pins a deal_engine_profile by uuid + version (bare
 *   profile keys are not unique).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Corpus document kinds (knowledge.document.source_kind values owned by BPR)
// ---------------------------------------------------------------------------
/** Ingested authoritative source content feeding framework generation. */
export const BPR_SOURCE_DOCUMENT_KIND = 'bpr_source';
/** Mirror of a discovery note, embedded for semantic retrieval in synthesis. */
export const BPR_NOTE_DOCUMENT_KIND = 'bpr_note';
// ---------------------------------------------------------------------------
// Value vocabularies (app-owned enum sets for plain-text DB columns)
// ---------------------------------------------------------------------------
export const BPR_FRAMEWORK_STATUSES = ['draft', 'approved', 'archived', 'fallback'];
export const bprFrameworkStatusSchema = z.enum(BPR_FRAMEWORK_STATUSES);
export const BPR_FRAMEWORK_SCOPES = ['global', 'org'];
export const bprFrameworkScopeSchema = z.enum(BPR_FRAMEWORK_SCOPES);
export const BPR_OVERRIDE_SCOPES = ['org', 'customer'];
export const bprOverrideScopeSchema = z.enum(BPR_OVERRIDE_SCOPES);
export const BPR_OVERRIDE_OPERATIONS = ['add', 'modify', 'suppress', 'reprioritize'];
export const bprOverrideOperationSchema = z.enum(BPR_OVERRIDE_OPERATIONS);
export const BPR_OVERRIDE_STATUSES = ['active', 'retired'];
export const BPR_FEEDBACK_ACTIONS = ['approve', 'reject', 'edit', 'rate', 'comment'];
export const bprFeedbackActionSchema = z.enum(BPR_FEEDBACK_ACTIONS);
export const BPR_SOURCE_TYPES = [
    'netsuite_docs',
    'oracle_docs',
    'methodology',
    'blog',
    'community',
    'other',
];
export const bprSourceTypeSchema = z.enum(BPR_SOURCE_TYPES);
export const BPR_SOURCE_STATUSES = ['active', 'retired'];
export const bprSourceStatusSchema = z.enum(BPR_SOURCE_STATUSES);
export const BPR_ENGAGEMENT_STATUSES = [
    'draft',
    'active',
    'synthesis',
    'completed',
    'archived',
];
export const bprEngagementStatusSchema = z.enum(BPR_ENGAGEMENT_STATUSES);
export const BPR_SESSION_STATUSES = ['planned', 'held', 'processed', 'cancelled'];
export const bprSessionStatusSchema = z.enum(BPR_SESSION_STATUSES);
export const BPR_NOTE_SOURCE_TYPES = ['manual', 'transcript'];
export const bprNoteSourceTypeSchema = z.enum(BPR_NOTE_SOURCE_TYPES);
export const BPR_FINDING_STATUSES = ['draft', 'confirmed', 'dismissed'];
export const bprFindingStatusSchema = z.enum(BPR_FINDING_STATUSES);
export const BPR_RECOMMENDATION_STATUSES = ['draft', 'confirmed', 'rejected'];
export const bprRecommendationStatusSchema = z.enum(BPR_RECOMMENDATION_STATUSES);
// 'diagrams' = the visual output layer (system-architecture pair + per-area
// workflow specs) - generated from the knowledge graph via /diagrams, not
// from confirmed recommendations like the other kinds.
export const BPR_OUTPUT_KINDS = ['sow', 'estimate', 'resource_plan', 'diagrams'];
export const bprOutputKindSchema = z.enum(BPR_OUTPUT_KINDS);
export const BPR_OUTPUT_STATUSES = ['generated', 'superseded'];
// ---------------------------------------------------------------------------
// Framework content (the generated "inverted triangle" document)
// ---------------------------------------------------------------------------
/** A citation attaching a generated node to an ingested source. */
export const bprCitationSchema = z.object({
    /** knowledge.bpr_framework_source uuid, when the source is registered. */
    sourceUuid: z.string().uuid().optional(),
    /** knowledge.document uuid of the ingested content the claim rests on. */
    documentUuid: z.string().uuid().optional(),
    title: z.string().optional(),
    uri: z.string().optional(),
    /** Short supporting excerpt from the source. */
    snippet: z.string().optional(),
});
/**
 * One of the top solution patterns for an issue - the X in X + Y = Z
 * (product knowledge matched against a business variable to reach a
 * recommendation).
 */
export const bprSolutionPatternSchema = z.object({
    /** Stable slug key, unique within the issue (e.g. 'advanced_billing'). */
    key: z.string().min(1),
    title: z.string().min(1),
    /** What the pattern is and how it solves the issue. */
    description: z.string().optional(),
    /**
     * Product capability this pattern leans on, by product key
     * (platform.product.product_key, e.g. 'oracle:netsuite') plus free-form
     * feature/module names - there is no feature-level catalog to FK into
     * (Phase 0 finding), so feature naming lives here as stable text.
     */
    productKey: z.string().optional(),
    features: z.array(z.string()).optional(),
    /** Business variables (the Y) that make this pattern the right fit. */
    fitCriteria: z.array(z.string()).optional(),
    /** Known limitations / when NOT to choose this pattern. */
    tradeoffs: z.array(z.string()).optional(),
    citations: z.array(bprCitationSchema).optional(),
    /** Generation confidence 0..1 for this node. */
    confidence: z.number().min(0).max(1).optional(),
});
/** A prioritized issue within a functional area (one ring of the triangle). */
export const bprIssueSchema = z.object({
    /** Stable slug key, unique within the area (e.g. 'invoicing'). */
    key: z.string().min(1),
    title: z.string().min(1),
    description: z.string().optional(),
    /** Priority rank within the area: 1 = most important. */
    rank: z.number().int().min(1),
    /** Why this issue matters / what breaks when it is unaddressed. */
    importance: z.string().optional(),
    /**
     * The business variables discovery should gather to pick a pattern
     * (question prompts for classification, e.g. 'invoice volume per month').
     */
    discoveryVariables: z.array(z.string()).optional(),
    /** Top 2-3 known ways to solve the issue, best fit first. */
    patterns: z.array(bprSolutionPatternSchema),
    citations: z.array(bprCitationSchema).optional(),
    confidence: z.number().min(0).max(1).optional(),
});
/** A functional area (e.g. order to cash) with its ranked issues. */
export const bprFunctionalAreaSchema = z.object({
    /** Stable slug key, unique within the framework (e.g. 'order_to_cash'). */
    key: z.string().min(1),
    title: z.string().min(1),
    description: z.string().optional(),
    /** Display/priority order of the area within the framework. */
    rank: z.number().int().min(1),
    /** Ranked issues, most important first. */
    issues: z.array(bprIssueSchema),
    citations: z.array(bprCitationSchema).optional(),
    confidence: z.number().min(0).max(1).optional(),
});
/**
 * The full generated framework document stored in
 * knowledge.bpr_framework_version.content and frozen onto
 * sales.bpr_engagement.resolved_framework at pin time.
 */
export const bprFrameworkContentSchema = z.object({
    /** Tool/platform dimension; aligns with platform.product.product_key. */
    toolKey: z.string().min(1),
    /** One-paragraph methodology summary for the review screen. */
    summary: z.string().optional(),
    areas: z.array(bprFunctionalAreaSchema),
});
/** One entry of a version's source manifest (what fed the generation). */
export const bprSourceManifestEntrySchema = z.object({
    sourceUuid: z.string().uuid().optional(),
    documentUuid: z.string().uuid().optional(),
    title: z.string().optional(),
    sourceType: bprSourceTypeSchema.optional(),
    trustWeight: z.number().min(0).max(1).optional(),
    lastIngestedAt: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Override patches
// ---------------------------------------------------------------------------
/**
 * Payload of an override patch. Which fields apply depends on `operation`:
 * - add: `node` is the new area/issue/pattern to insert under `targetPath`'s
 *   parent scope (the path names the parent for adds one level down).
 * - modify: `node` carries the fields to overwrite on the target node.
 * - reprioritize: `rank` is the new rank of the target node.
 * - suppress: optional `reason`; the target node is removed from the
 *   resolved framework.
 */
export const bprOverridePayloadSchema = z.object({
    node: z.unknown().optional(),
    rank: z.number().int().min(1).optional(),
    reason: z.string().optional(),
});
export const bprOverrideSchema = z.object({
    uuid: z.string().uuid(),
    scope: bprOverrideScopeSchema,
    customerUuid: z.string().uuid().nullable().optional(),
    toolKey: z.string().min(1),
    /** Stable-key locator: 'areaKey' | 'areaKey.issueKey' | 'areaKey.issueKey.patternKey'. */
    targetPath: z.string().min(1),
    operation: bprOverrideOperationSchema,
    payload: bprOverridePayloadSchema.nullable().optional(),
    status: z.enum(BPR_OVERRIDE_STATUSES),
});
// ---------------------------------------------------------------------------
// Estimate config pinning
// ---------------------------------------------------------------------------
/**
 * Pin of the estimate configuration a recommendation is priced against.
 * Mirrors how deal_estimate snapshots profileId + profileVersion: a
 * deal_engine_profile is identified by uuid + version, never by a bare key
 * (the natural key is a 5-part composite).
 */
export const bprEstimateConfigRefSchema = z.object({
    profileUuid: z.string().uuid(),
    profileVersion: z.string().min(1),
    productKey: z.string().optional(),
    categoryKey: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Entity DTOs (API wire shapes; uuid-only, never internal ids)
// ---------------------------------------------------------------------------
export const bprFrameworkVersionSummarySchema = z.object({
    uuid: z.string().uuid(),
    toolKey: z.string(),
    scope: bprFrameworkScopeSchema,
    status: bprFrameworkStatusSchema,
    confidence: z.number().nullable().optional(),
    modelRef: z.string().nullable().optional(),
    promptRef: z.string().nullable().optional(),
    generatedAt: z.string().nullable().optional(),
    approvedAt: z.string().nullable().optional(),
    approvedByUserUuid: z.string().nullable().optional(),
    createdAt: z.string(),
    /** Node counts for list screens: { areas, issues, patterns }. */
    stats: z
        .object({
        areas: z.number().int(),
        issues: z.number().int(),
        patterns: z.number().int(),
    })
        .optional(),
});
export const bprFrameworkVersionDetailSchema = bprFrameworkVersionSummarySchema.extend({
    content: bprFrameworkContentSchema,
    sourceManifest: z.array(bprSourceManifestEntrySchema).nullable().optional(),
});
export const bprSourceSchema = z.object({
    uuid: z.string().uuid(),
    toolKey: z.string(),
    sourceType: bprSourceTypeSchema,
    title: z.string(),
    uri: z.string().nullable().optional(),
    trustWeight: z.number(),
    status: bprSourceStatusSchema,
    documentUuid: z.string().uuid().nullable().optional(),
    lastIngestedAt: z.string().nullable().optional(),
    createdAt: z.string(),
});
export const bprEngagementSchema = z.object({
    uuid: z.string().uuid(),
    companyUuid: z.string().uuid(),
    companyName: z.string().optional(),
    /** Set when the engagement was auto-created from a deal_category='bpr' deal. */
    dealUuid: z.string().uuid().nullable().optional(),
    primaryContactCustomerUuid: z.string().uuid().nullable().optional(),
    toolKey: z.string(),
    title: z.string(),
    status: bprEngagementStatusSchema,
    budgetedHours: z.number().nullable().optional(),
    futureStateGoals: z.array(z.string()).nullable().optional(),
    agreedTechStack: z
        .array(z.object({ name: z.string(), productKey: z.string().optional(), notes: z.string().optional() }))
        .nullable()
        .optional(),
    frameworkVersionUuid: z.string().uuid().nullable().optional(),
    frameworkPinnedAt: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const bprSessionSchema = z.object({
    uuid: z.string().uuid(),
    engagementUuid: z.string().uuid(),
    functionalAreaKey: z.string().nullable().optional(),
    title: z.string(),
    scheduledAt: z.string().nullable().optional(),
    durationHours: z.number().nullable().optional(),
    meetingRef: z.string().nullable().optional(),
    transcriptDocumentUuid: z.string().uuid().nullable().optional(),
    status: bprSessionStatusSchema,
    createdAt: z.string(),
});
export const bprDiscoveryNoteSchema = z.object({
    uuid: z.string().uuid(),
    engagementUuid: z.string().uuid(),
    sessionUuid: z.string().uuid(),
    rawText: z.string(),
    sourceType: bprNoteSourceTypeSchema,
    sourceRef: z.string().nullable().optional(),
    createdAt: z.string(),
});
export const bprBusinessVariableSchema = z.object({
    key: z.string(),
    value: z.string(),
    evidence: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Salesforce simple problem / solution synthesis contracts (Plan 47)
// ---------------------------------------------------------------------------
export const BPR_PROBLEM_PRIORITIES = ['p1', 'p2', 'p3'];
export const bprProblemPrioritySchema = z.enum(BPR_PROBLEM_PRIORITIES);
export const BPR_PROBLEM_STAGES = ['current_pain', 'planned', 'nice_to_have'];
export const bprProblemStageSchema = z.enum(BPR_PROBLEM_STAGES);
export const BPR_PROBLEM_SCOPES = ['in_scope', 'out_of_scope', 'unclear'];
export const bprProblemScopeSchema = z.enum(BPR_PROBLEM_SCOPES);
export const bprProblemEvidenceSchema = z.object({
    noteUuid: z.string(),
    quote: z.string(),
    speaker: z.string().nullable().optional(),
    sourceHash: z.string().optional(),
    span: z.object({ start: z.number(), end: z.number() }).optional(),
});
export const bprProblemMetaSchema = z.object({
    priority: bprProblemPrioritySchema,
    priorityReason: z.string().optional(),
    stage: bprProblemStageSchema.optional(),
    scope: bprProblemScopeSchema.optional(),
    scopeReason: z.string().optional(),
    evidence: z.array(bprProblemEvidenceSchema).default([]),
    confidence: z.number().min(0).max(1).optional(),
    stableRank: z.number().int().optional(),
    objectTags: z.array(z.string()).optional(),
});
export const BPR_MENU_VERDICTS = ['matched', 'declined', 'no_menu'];
export const bprMenuVerdictSchema = z.enum(BPR_MENU_VERDICTS);
export const BPR_MENU_VALIDATION_STATUSES = [
    'valid_match',
    'valid_decline',
    'no_menu',
    'invalid_match',
];
export const bprMenuValidationStatusSchema = z.enum(BPR_MENU_VALIDATION_STATUSES);
export const bprSimpleSynthesisMetadataSchema = z.object({
    schemaVersion: z.literal('1.0').default('1.0'),
    pipelineId: z.string(),
    problemBatchId: z.string(),
    solutionBatchId: z.string(),
    runId: z.string().optional(),
    parentRunId: z.string().optional(),
    stage: z.literal('solutions').default('solutions'),
    generationMethod: z.literal('model').default('model'),
    menuVerdict: bprMenuVerdictSchema,
    menuValidationStatus: bprMenuValidationStatusSchema,
    declineReason: z.string().nullable().optional(),
    drivers: z.record(z.any()).default({}),
    rationale: z.string().optional(),
    assumptions: z.array(z.string()).default([]),
    risks: z.array(z.string()).default([]),
    dependencies: z.array(z.string()).default([]),
    confidence: z.number().min(0).max(1).optional(),
    stableRank: z.number().int().optional(),
});
/**
 * Extracts simpleSynthesis carrier metadata from recommendation object or configNotes array if present.
 */
export function readBprSimpleSynthesisMetadata(source) {
    const notes = Array.isArray(source)
        ? source
        : source && typeof source === 'object' && 'configNotes' in source
            ? source.configNotes
            : null;
    if (!Array.isArray(notes))
        return null;
    for (const item of notes) {
        if (item && typeof item === 'object' && 'simpleSynthesis' in item) {
            const parsed = bprSimpleSynthesisMetadataSchema.safeParse(item.simpleSynthesis);
            if (parsed.success)
                return parsed.data;
        }
    }
    return null;
}
export const bprSynthesisRunSummarySchema = z.object({
    runId: z.number(),
    stage: z.enum(['problems', 'solutions']),
    batchId: z.string(),
    pipelineId: z.string().optional(),
    status: z.enum(['completed', 'in_progress', 'failed', 'partial', 'not_applied', 'empty']),
    proposedCount: z.number(),
    appliedCount: z.number(),
    quarantinedCount: z.number().default(0),
    reviewReasons: z.array(z.string()).default([]),
    createdAt: z.string(),
    updatedAt: z.string().optional(),
});
export const bprSynthesisRunsResponseSchema = z.object({
    data: z.array(bprSynthesisRunSummarySchema),
});
export const bprFindingSchema = z.object({
    uuid: z.string().uuid(),
    engagementUuid: z.string().uuid(),
    sessionUuid: z.string().uuid().nullable().optional(),
    functionalAreaKey: z.string(),
    issueKey: z.string(),
    currentStateSummary: z.string().nullable().optional(),
    businessVariables: z.array(bprBusinessVariableSchema).nullable().optional(),
    problemMeta: bprProblemMetaSchema.nullable().optional(),
    classificationConfidence: z.number().nullable().optional(),
    sourceNoteUuids: z.array(z.string().uuid()).nullable().optional(),
    status: bprFindingStatusSchema,
    createdAt: z.string(),
});
export const bprRecommendationSchema = z.object({
    uuid: z.string().uuid(),
    engagementUuid: z.string().uuid(),
    findingUuid: z.string().uuid().nullable().optional(),
    categoryKey: z.string().nullable().optional(),
    issueKey: z.string(),
    solutionPatternKey: z.string().nullable().optional(),
    title: z.string(),
    futureStateProcess: z.string().nullable().optional(),
    configNotes: z
        .array(z.object({
        note: z.string(),
        refs: z.array(z.string()).optional(),
        simpleSynthesis: bprSimpleSynthesisMetadataSchema.optional(),
    }))
        .nullable()
        .optional(),
    estimateConfigRef: bprEstimateConfigRefSchema.nullable().optional(),
    estimatedHours: z.number().nullable().optional(),
    status: bprRecommendationStatusSchema,
    createdAt: z.string(),
});
export const bprOutputSchema = z.object({
    uuid: z.string().uuid(),
    engagementUuid: z.string().uuid(),
    kind: bprOutputKindSchema,
    content: z.unknown().nullable().optional(),
    contentMarkdown: z.string().nullable().optional(),
    sourceRefs: z
        .object({
        recommendationUuids: z.array(z.string().uuid()).optional(),
        estimateOutputUuid: z.string().uuid().optional(),
        profileRefs: z.array(bprEstimateConfigRefSchema).optional(),
    })
        .nullable()
        .optional(),
    version: z.number().int(),
    generatedAt: z.string().nullable().optional(),
    status: z.enum(BPR_OUTPUT_STATUSES),
    createdAt: z.string(),
});
// ---------------------------------------------------------------------------
// Request contracts (route validation)
// ---------------------------------------------------------------------------
export const bprCreateEngagementRequestSchema = z.object({
    companyUuid: z.string().uuid(),
    primaryContactCustomerUuid: z.string().uuid().optional(),
    toolKey: z.string().min(1),
    title: z.string().min(1),
    budgetedHours: z.number().positive().optional(),
    futureStateGoals: z.array(z.string()).optional(),
    agreedTechStack: z
        .array(z.object({ name: z.string(), productKey: z.string().optional(), notes: z.string().optional() }))
        .optional(),
});
export const bprUpdateEngagementRequestSchema = bprCreateEngagementRequestSchema
    .partial()
    .extend({ status: bprEngagementStatusSchema.optional() });
export const bprCreateSessionRequestSchema = z.object({
    title: z.string().min(1),
    functionalAreaKey: z.string().optional(),
    scheduledAt: z.string().optional(),
    durationHours: z.number().positive().optional(),
    meetingRef: z.string().optional(),
});
export const bprUpdateSessionRequestSchema = bprCreateSessionRequestSchema
    .partial()
    .extend({ status: bprSessionStatusSchema.optional() });
export const bprCreateNoteRequestSchema = z.object({
    rawText: z.string().min(1),
    sourceType: bprNoteSourceTypeSchema.optional(),
    sourceRef: z.string().optional(),
});
export const bprRegisterSourceRequestSchema = z.object({
    toolKey: z.string().min(1),
    sourceType: bprSourceTypeSchema,
    title: z.string().min(1),
    uri: z.string().optional(),
    trustWeight: z.number().min(0).max(1).optional(),
    /** Pasted source content; ingested immediately as a knowledge.document. */
    contentText: z.string().optional(),
});
export const bprUpdateSourceRequestSchema = z.object({
    title: z.string().min(1).optional(),
    uri: z.string().optional(),
    trustWeight: z.number().min(0).max(1).optional(),
    status: bprSourceStatusSchema.optional(),
});
export const bprCreateOverrideRequestSchema = z.object({
    scope: bprOverrideScopeSchema,
    customerUuid: z.string().uuid().optional(),
    toolKey: z.string().min(1),
    targetPath: z.string().min(1),
    operation: bprOverrideOperationSchema,
    payload: bprOverridePayloadSchema.optional(),
});
export const bprFeedbackRequestSchema = z.object({
    targetPath: z.string().optional(),
    action: bprFeedbackActionSchema,
    rating: z.number().min(1).max(5).optional(),
    comment: z.string().optional(),
});
export const bprGenerateFrameworkRequestSchema = z.object({
    toolKey: z.string().min(1),
    /** Restrict generation to one functional area (incremental refresh). */
    functionalAreaKey: z.string().optional(),
});
export const bprCreateFindingRequestSchema = z.object({
    sessionUuid: z.string().uuid().optional(),
    functionalAreaKey: z.string().min(1),
    issueKey: z.string().min(1),
    currentStateSummary: z.string().optional(),
    businessVariables: z.array(bprBusinessVariableSchema).optional(),
    problemMeta: bprProblemMetaSchema.optional(),
    classificationConfidence: z.number().min(0).max(1).optional(),
    sourceNoteUuids: z.array(z.string().uuid()).optional(),
});
export const bprUpdateFindingRequestSchema = bprCreateFindingRequestSchema
    .partial()
    .extend({ status: bprFindingStatusSchema.optional() });
export const bprCreateRecommendationRequestSchema = z.object({
    findingUuid: z.string().uuid().optional(),
    categoryKey: z.string().optional(),
    issueKey: z.string().min(1),
    solutionPatternKey: z.string().optional(),
    title: z.string().min(1),
    futureStateProcess: z.string().optional(),
    configNotes: z
        .array(z.object({
        note: z.string(),
        refs: z.array(z.string()).optional(),
        simpleSynthesis: bprSimpleSynthesisMetadataSchema.optional(),
    }))
        .optional(),
    estimateConfigRef: bprEstimateConfigRefSchema.optional(),
    estimatedHours: z.number().nonnegative().optional(),
});
export const bprUpdateRecommendationRequestSchema = bprCreateRecommendationRequestSchema
    .partial()
    .extend({
    status: bprRecommendationStatusSchema.optional(),
    reselectPriorUuid: z.string().uuid().optional(),
});
//# sourceMappingURL=bpr.js.map